// Persisted Work failures may only retain a message, while API refusals carry
// structured details. Recognize both without treating run/token limits as money.
export function isWorkCreditError(error) {
  if (!error) return false;
  const code = error?.data?.reason || error?.data?.errorCode || error?.reason || error?.code || error?.errorCode;
  if (['insufficient_credits', 'insufficient_credit_authorization', 'credits_exhausted', 'credits_reserved'].includes(code)) return true;
  if (error?.status === 402) return true;
  const message = typeof error === 'string' ? error : error?.message || error?.error || error?.data?.message || '';
  return /insufficient (?:purchased )?credits|not enough credits|out of credits|credits (?:ran out|have run out|exhausted)|this request can use up to [\d,.]+ credits.*credits are available|(?:running|in-flight) (?:tasks|requests).*reserved credits/i.test(message);
}

export function workCreditRequirement(error) {
  const data = error?.data?.data || error?.data || error || {};
  const structured = Number(data.authorizationCredits ?? data.requiredCredits ?? data.estimatedCredits);
  if (Number.isFinite(structured) && structured > 0) return Math.ceil(structured);
  const message = typeof error === 'string' ? error : error?.message || error?.error || '';
  const match = message.match(/(?:can use up to|requires?|need(?:s)?|temporary authorization of)\s+([\d,]+(?:\.\d+)?)\s+credits/i);
  return match ? Math.ceil(Number(match[1].replace(/,/g, ''))) : null;
}

export function workCreditRecoveryState({ error, balance, initialBalance, requiredCredits, balanceChecked = false, reservedCredits }) {
  const available = Math.max(0, Number(balance) || 0);
  const required = Number(requiredCredits) > 0 ? Math.ceil(Number(requiredCredits)) : workCreditRequirement(error);
  const message = typeof error === 'string' ? error : error?.message || error?.error || '';
  const quoted = message.match(/(?:only )([\d,.]+) credits are available/i);
  const refusedBalance = quoted ? Number(quoted[1].replace(/,/g, '')) : null;
  const contradictory = required && refusedBalance != null && refusedBalance >= required;
  const historicalReservation = !!contradictory || /holds on running work|credits.*reserved|reserved credits|credits \(in-flight\)/i.test(message)
    || [error?.code, error?.reason, error?.data?.reason, error?.data?.code].includes('credits_reserved');
  const liveReservations = Number.isFinite(reservedCredits);
  const reserved = liveReservations ? reservedCredits > 0 && (!required || available < required) : historicalReservation;
  const exhausted = /credits (?:ran out|have run out|exhausted)/i.test(message) || error?.reason === 'credits_exhausted';
  const ready = available > 0 && (required ? available >= required : available > (exhausted ? 0 : (initialBalance ?? available)))
    && !reserved;
  return { available, required, ready, reserved, shortfall: required ? Math.max(0, Math.ceil(required - available)) : null };
}
